// src/pages/ClinicalDataCollector/components/output/AFSection.jsx

import React, { useMemo } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import SpeedIcon from '@mui/icons-material/Speed';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import LockIcon from '@mui/icons-material/Lock';
import PeopleIcon from '@mui/icons-material/People';
import { useSelector } from 'react-redux';

// 👇 nuevo: data simulada
import afSectionData from '../data/afSectionData.json';

const AFSection = () => {
  const { PatientSelect } = useSelector((state) => state.patientMapSelect || {});
  const patients = afSectionData?.patients || [];

  const selectedPatient = useMemo(() => {
    if (!patients.length) return null;

    if (PatientSelect?.memberId) {
      const base =
        (PatientSelect.memberId.length || 1) +
        (PatientSelect.age || 0) * 19;
      const idx = Math.abs(base) % patients.length;
      return patients[idx];
    }

    const randomIdx = Math.floor(Math.random() * patients.length);
    return patients[randomIdx];
  }, [PatientSelect, patients]);

  if (!selectedPatient) return null;

  const {
    name,
    profile,
    code,
    description,
    rhythmControl,
    rateControl,
    anticoagulation,
    appendageClosure,
    benefitConsiderations
  } = selectedPatient;

  return (
    <Card elevation={3} sx={{ borderRadius: 2, mb: 4 }}>
      <CardHeader
        title="Section: Atrial Fibrillation"
        // subheader={`Simulated profile: ${name} — ${profile}`}
        titleTypographyProps={{ variant: 'h5', align: 'center' }}
        action={<SyncIcon fontSize="large" color="primary" />}
      />
      <Divider />
      <CardContent>
        <Typography
          variant="subtitle1"
          sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center' }}
        >
          <PeopleIcon sx={{ mr: 1 }} /> Code: {code} — {description}
        </Typography>

        <Box sx={{ display: 'flex', gap: 4, mb: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
            >
              <DeviceHubIcon sx={{ mr: 1 }} /> Rhythm Control
            </Typography>
            <List dense>
              {rhythmControl.map((item, i) => (
                <ListItem key={i} disablePadding>
                  <ListItemIcon>
                    <SyncIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={item} />
                </ListItem>
              ))}
            </List>
          </Box>

          <Box>
            <Typography
              variant="subtitle2"
              sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
            >
              <SpeedIcon sx={{ mr: 1 }} /> Rate Control
            </Typography>
            <List dense>
              {rateControl.map((item, i) => (
                <ListItem key={i} disablePadding>
                  <ListItemIcon>
                    <SpeedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={item} />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>

        <Typography
          variant="subtitle2"
          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
        >
          <LocalHospitalIcon sx={{ mr: 1 }} /> Anticoagulation
        </Typography>
        <List dense sx={{ mb: 2 }}>
          {anticoagulation.map((item, i) => (
            <ListItem key={i} disablePadding>
              <ListItemIcon>
                <LocalHospitalIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={item} />
            </ListItem>
          ))}
        </List>

        <Typography
          variant="subtitle2"
          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
        >
          <LockIcon sx={{ mr: 1 }} /> Left Atrial Appendage Closure
        </Typography>
        <List dense sx={{ mb: 2 }}>
          {appendageClosure.map((item, i) => (
            <ListItem key={i} disablePadding>
              <ListItemIcon>
                <LockIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={item} />
            </ListItem>
          ))}
        </List>

        <Typography
          variant="subtitle2"
          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
        >
          <PeopleIcon sx={{ mr: 1 }} /> Benefits and Considerations
        </Typography>
        <List dense>
          {benefitConsiderations.map((item, i) => (
            <ListItem key={i} disablePadding>
              <ListItemIcon>
                <PeopleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={item} />
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

export default AFSection;
